import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  checkAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const initializeAuth = useCallback(() => {
    // Try localStorage first
    let token = localStorage.getItem('token');
    let userData = localStorage.getItem('user');
    
    // If localStorage fails, try sessionStorage
    if (!token || !userData) {
      token = sessionStorage.getItem('token');
      userData = sessionStorage.getItem('user');
      
      // If found in sessionStorage, restore to localStorage
      if (token && userData) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', userData);
      }
    }
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
        return true;
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
        return false;
      }
    }
    setUser(null);
    setIsAuthenticated(false);
    return false;
  }, []);

  useEffect(() => {
    // Small delay to ensure localStorage is ready
    const timer = setTimeout(() => {
      initializeAuth();
      setIsLoading(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [initializeAuth]);

  const login = async (username: string, password: string) => {
    try {
      const response = await authAPI.login({ username, password });
      const { token, id, username: userName, email } = response.data;
      
      // Save to both localStorage and sessionStorage as backup
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify({ id, username: userName, email }));
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify({ id, username: userName, email }));
      
      setUser({ id, username: userName, email });
      setIsAuthenticated(true);
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Invalid username or password');
      } else if (error.response?.status === 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error('Login failed. Please check your connection.');
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const checkAuth = useCallback(() => {
    return initializeAuth();
  }, [initializeAuth]);

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, isLoading, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};